import {
    math,
    ADDRESS_CLAMP_TO_EDGE,
    ASPECT_MANUAL,
    BLENDEQUATION_ADD,
    BLENDMODE_ONE,
    BLENDMODE_ONE_MINUS_SRC_ALPHA,
    FILTER_LINEAR,
    FILTER_NEAREST,
    PIXELFORMAT_RGBA8,
    PIXELFORMAT_RGBA16F,
    PIXELFORMAT_DEPTH,
    PROJECTION_ORTHOGRAPHIC,
    PROJECTION_PERSPECTIVE,
    TONEMAP_ACES,
    TONEMAP_ACES2,
    TONEMAP_FILMIC,
    TONEMAP_HEJL,
    TONEMAP_LINEAR,
    TONEMAP_NEUTRAL,
    BoundingBox,
    BlendState,
    Color,
    Entity,
    Mat4,
    Quat,
    Ray,
    RenderPass,
    RenderPassForward,
    RenderTarget,
    Texture,
    Vec3,
    Vec4
} from 'playcanvas';

import type { CommandContext } from './command-queue';
import { PointerController } from './controllers';
import { Element, ElementType } from './element';
import { Picker } from './picker';
import { Serializer } from './serializer';
import { vertexShader, fragmentShader } from './shaders/blit-shader';
import { fragmentShader as stochasticResolveShader } from './shaders/stochastic-warp-resolve-shader';
import { Splat } from './splat';
import { TweenValue } from './tween-value';
import { ShaderQuad, SimpleRenderPass } from './utils/simple-render-pass';

// work globals
const forwardVec = new Vec3();
const cameraPosition = new Vec3();
const ray = new Ray();
const vec = new Vec3();
const vecb = new Vec3();
const vec4 = new Vec4();
const invViewProj = new Mat4();

// snapshot of everything a pick or screen-space selection depends on, taken
// at gesture time so work that runs from the command queue later sees the
// camera and target the user saw. Treated as immutable: readonly doesn't
// protect the vectors and matrices, so consumers must not mutate them.
type CameraFrame = {
    readonly position: Vec3;
    readonly rotation: Quat;
    readonly fov: number;
    readonly ortho: boolean;
    readonly orthoHeight: number;
    readonly near: number;
    readonly far: number;
    readonly horizontalFov: boolean;
    readonly aspectRatio: number;
    // the camera component's matrices (GL clip range; shaders apply
    // Camera.applyShaderProjectionTransform as the frame loop does)
    readonly projection: Mat4;
    readonly view: Mat4;
    // css pixels govern brush radii; render-target pixels govern picking and
    // packed selection regions
    readonly viewport: { readonly width: number, readonly height: number };
    readonly target: { readonly width: number, readonly height: number };
    // world size of one css pixel at view depth 1 (depth-independent in ortho)
    readonly pixelScale: number;
};
const va = new Vec3();
const m = new Mat4();
const v4 = new Vec4();

// modulo dealing with negative numbers
const mod = (n: number, m: number) => ((n % m) + m) % m;

class Camera extends Element {
    /**
     * Calculate the forward vector given azimuth and elevation angles.
     *
     * @param {Vec3} result - The Vec3 to store the result in.
     * @param {number} azim - Azimuth angle in degrees.
     * @param {number} elev - Elevation angle in degrees.
     */
    static calcForwardVec(result: Vec3, azim: number, elev: number) {
        const ex = elev * math.DEG_TO_RAD;
        const ey = azim * math.DEG_TO_RAD;
        const s1 = Math.sin(-ex);
        const c1 = Math.cos(-ex);
        const s2 = Math.sin(-ey);
        const c2 = Math.cos(-ey);
        result.set(-c1 * s2, s1, c1 * c2);
    }

    controller: PointerController;
    focalPointTween = new TweenValue({ x: 0, y: 0.5, z: 0 });
    azimElevTween = new TweenValue({ azim: 30, elev: -15 });
    distanceTween = new TweenValue({ distance: 1 });

    minElev = -90;
    maxElev = 90;

    sceneRadius = 1;

    flySpeed = 1;

    controlMode: 'orbit' | 'fly' = 'orbit';

    // during fly-mode look, stores the camera position that must stay fixed
    // while the azim/elev tween smoothly converges
    lookCameraPos: Vec3 | null = null;

    picker: Picker;

    mainCamera: Entity;

    mainTarget: RenderTarget;
    splatTarget: RenderTarget;
    colorTarget: RenderTarget;
    workTarget: RenderTarget;
    stochasticTarget: RenderTarget;
    warpTarget: RenderTarget;

    // Render passes
    clearPass: RenderPass;
    stochasticClearPass: RenderPass;
    warpClearPass: RenderPass;
    mainPass: RenderPassForward;
    splatPass: RenderPassForward;
    stochasticSplatPass: RenderPassForward;
    warpSplatPass: RenderPassForward;
    stochasticResolvePass: SimpleRenderPass;
    gizmoPass: RenderPassForward;
    depthReducePass: RenderPass;
    finalPass: SimpleRenderPass;

    // overridden target size
    targetSizeOverride: { width: number, height: number } = null;

    // when set, overrides the tween-driven pose, fov and clipping planes each
    // update (used by 360 capture to render arbitrary face orientations that
    // the azim/elev pose system cannot express)
    poseOverride: { position: Vec3, rotation: Quat, fov: number, near: number, far: number } | null = null;

    // world transform of the user-facing camera pose. while a pose override
    // is active this holds the last tween-driven pose, so ui elements (view
    // cube, overlays) don't track the internal capture poses
    displayTransform = new Mat4();

    renderOverlays = true;

    updateCameraUniforms: () => void;

    constructor() {
        super(ElementType.camera);

        // create the camera entity
        this.mainCamera = new Entity('Camera');
        this.mainCamera.addComponent('camera');
    }

    // ortho
    set ortho(value: boolean) {
        if (value !== this.ortho) {
            this.camera.projection = value ? PROJECTION_ORTHOGRAPHIC : PROJECTION_PERSPECTIVE;
            this.scene.events.fire('camera.ortho', value);
        }
    }

    get ortho() {
        return this.camera.projection === PROJECTION_ORTHOGRAPHIC;
    }

    // fov
    set fov(value: number) {
        this.camera.fov = value;
    }

    get fov() {
        return this.camera.fov;
    }

    // tonemapping
    set tonemapping(value: string) {
        const mapping: Record<string, number> = {
            linear: TONEMAP_LINEAR,
            neutral: TONEMAP_NEUTRAL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL
        };

        const tvalue = mapping[value];

        if (tvalue !== undefined && tvalue !== this.camera.toneMapping) {
            this.camera.toneMapping = tvalue;
            this.scene.events.fire('camera.tonemapping', value);
        }
    }

    get tonemapping() {
        switch (this.camera.toneMapping) {
            case TONEMAP_LINEAR: return 'linear';
            case TONEMAP_NEUTRAL: return 'neutral';
            case TONEMAP_ACES: return 'aces';
            case TONEMAP_ACES2: return 'aces2';
            case TONEMAP_FILMIC: return 'filmic';
            case TONEMAP_HEJL: return 'hejl';
        }
        return 'linear';
    }

    // near clip
    set near(value: number) {
        this.camera.nearClip = value;
    }

    get near() {
        return this.camera.nearClip;
    }

    // far clip
    set far(value: number) {
        this.camera.farClip = value;
    }

    get far() {
        return this.camera.farClip;
    }

    // focal point
    get focalPoint() {
        const t = this.focalPointTween.target;
        return new Vec3(t.x, t.y, t.z);
    }

    // azimuth, elevation
    get azimElev() {
        return this.azimElevTween.target;
    }

    get azim() {
        return this.azimElev.azim;
    }

    get elevation() {
        return this.azimElev.elev;
    }

    get distance() {
        return this.distanceTween.target.distance;
    }

    setFocalPoint(point: Vec3, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;
        this.focalPointTween.goto(point, dampingFactorFactor * this.scene.config.controls.dampingFactor);
    }

    // Fly mode: rotate camera around itself, keeping the camera position fixed
    look(dx: number, dy: number) {
        const sensitivity = this.scene.config.controls.orbitSensitivity;
        const d = this.distance * this.sceneRadius / this.fovFactor;

        Camera.calcForwardVec(forwardVec, this.azim, this.elevation);
        const cameraPos = this.focalPoint.add(forwardVec.clone().mulScalar(d));

        const azim = this.azim - dx * sensitivity;
        const elev = this.elevation - dy * sensitivity;

        Camera.calcForwardVec(forwardVec, azim, elev);
        const focalPoint = cameraPos.clone().sub(forwardVec.clone().mulScalar(d));

        this.setAzimElev(azim, elev);
        this.focalPointTween.goto(focalPoint, this.scene.config.controls.dampingFactor);
        this.lookCameraPos = cameraPos;
    }

    setAzimElev(azim: number, elev: number, dampingFactorFactor: number = 1) {
        // clamp
        azim = mod(azim, 360);
        elev = Math.max(this.minElev, Math.min(this.maxElev, elev));

        const t = this.azimElevTween;
        t.goto({ azim, elev }, dampingFactorFactor * this.scene.config.controls.dampingFactor);

        // handle wraparound
        if (t.source.azim - azim < -180) {
            t.source.azim += 360;
        } else if (t.source.azim - azim > 180) {
            t.source.azim -= 360;
        }

        // return to perspective mode on rotation
        this.ortho = false;
    }

    setDistance(distance: number, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;

        const controls = this.scene.config.controls;

        // clamp
        distance = Math.max(controls.minZoom, Math.min(controls.maxZoom, distance));

        const t = this.distanceTween;
        t.goto({ distance }, dampingFactorFactor * controls.dampingFactor);
    }

    setPose(position: Vec3, target: Vec3, dampingFactorFactor: number = 1) {
        vec.sub2(target, position);
        const l = vec.length();
        const azim = Math.atan2(-vec.x / l, -vec.z / l) * math.RAD_TO_DEG;
        const elev = Math.asin(vec.y / l) * math.RAD_TO_DEG;
        this.setFocalPoint(target, dampingFactorFactor);
        this.setAzimElev(azim, elev, dampingFactorFactor);
        this.setDistance(l / this.sceneRadius * this.fovFactor, dampingFactorFactor);
    }

    // set or clear the pose override and apply it immediately so subsequent
    // splat sorting and rendering see the new transform
    setPoseOverride(override: Camera['poseOverride']) {
        this.poseOverride = override;
        this.onUpdate(0);
    }

    // transform the world space coordinate to normalized screen coordinate
    worldToScreen(world: Vec3, screen: Vec3) {
        const { camera } = this;
        m.mul2(camera.projectionMatrix, camera.viewMatrix);

        v4.set(world.x, world.y, world.z, 1);
        m.transformVec4(v4, v4);

        screen.x = v4.x / v4.w * 0.5 + 0.5;
        screen.y = 1.0 - (v4.y / v4.w * 0.5 + 0.5);
        screen.z = v4.z / v4.w;
    }

    add() {
        const { camera, scene } = this;

        scene.cameraRoot.addChild(this.mainCamera);

        // configure camera to render all layers
        this.mainCamera.camera.layers = [
            scene.worldLayer.id,
            scene.splatLayer.id,
            scene.overlayLayer.id,
            scene.centersLayer.id,
            scene.gizmoLayer.id
        ];

        // use manual aspect ratio mode so we can set it based on targetSize
        camera.aspectRatioMode = ASPECT_MANUAL;

        // create render passes
        const device = scene.graphicsDevice;
        const { app } = scene;
        const renderer = app.renderer;
        const composition = app.scene.layers;

        this.clearPass = new RenderPass(device);
        this.stochasticClearPass = new RenderPass(device);
        this.warpClearPass = new RenderPass(device);
        this.mainPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.splatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.stochasticSplatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.warpSplatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.stochasticResolvePass = new SimpleRenderPass(device,
            new ShaderQuad(device, vertexShader, stochasticResolveShader, 'stochastic-resolve'), {
                blendState: new BlendState(true, BLENDEQUATION_ADD, BLENDMODE_ONE, BLENDMODE_ONE_MINUS_SRC_ALPHA),
                vars: () => ({
                    srcTexture: this.stochasticTarget.colorBuffer,
                    // Zero bypasses undistortion without changing splat projection.
                    warpStrength: scene.warpedRender && scene.stochastic.undistort ? scene.stochastic.warpStrength - 1 : 0,
                    quadResolve: scene.stochastic.resolve ? 1 : 0
                })
            });
        this.gizmoPass = new RenderPassForward(device, composition, app.scene, renderer);
        // compute-only pass between the splat and gizmo passes - never given a
        // render target, so RenderPass.render runs it without opening one. It
        // folds a stochastic frame's splat depth into the projected renderer's
        // occlusion map before the gizmo pass clears the depth buffer
        this.depthReducePass = new RenderPass(device);
        this.depthReducePass.name = 'depthReduce';
        this.depthReducePass.execute = () => scene.projectedSplatRenderer.reduceDepth();
        this.finalPass = new SimpleRenderPass(device,
            new ShaderQuad(device, vertexShader, fragmentShader, 'final-blit'), {
                vars: () => {
                    const gd = this.scene.graphicsDevice;
                    const ts = this.targetSize;
                    return {
                        srcTexture: this.mainTarget.colorBuffer,
                        // upscale the (possibly lower-res) target to the backbuffer
                        blitScale: [ts.width / gd.width, ts.height / gd.height],
                        overdraw: this.scene.overdrawRender ? 1 : 0
                    };
                }
            });

        const target = document.getElementById('canvas-container');
        this.controller = new PointerController(this, target);

        // apply scene config
        const config = scene.config;
        const controls = config.controls;

        this.minElev = (controls.minPolarAngle * 180) / Math.PI - 90;
        this.maxElev = (controls.maxPolarAngle * 180) / Math.PI - 90;

        // tonemapping
        camera.toneMapping = {
            linear: TONEMAP_LINEAR,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            neutral: TONEMAP_NEUTRAL
        }[config.camera.toneMapping];

        // exposure
        scene.app.scene.exposure = config.camera.exposure;

        this.fov = config.camera.fov;

        // initial camera position and orientation
        this.setAzimElev(controls.initialAzim, controls.initialElev, 0);
        this.setDistance(controls.initialZoom, 0);

        // picker
        this.picker = new Picker(scene);

        scene.events.on('scene.boundChanged', this.onBoundChanged, this);

        // prepare camera-specific uniforms
        this.updateCameraUniforms = () => {
            const device = scene.graphicsDevice;
            const entity = this.mainCamera;
            const camera = entity.camera;

            const set = (name: string, vec: Vec3) => {
                device.scope.resolve(name).setValue([vec.x, vec.y, vec.z]);
            };

            // get frustum corners in world space
            const points = camera.camera.getFrustumCorners(-100);
            const worldTransform = this.worldTransform;
            for (let i = 0; i < points.length; i++) {
                worldTransform.transformPoint(points[i], points[i]);
            }

            // near
            if (camera.projection === PROJECTION_PERSPECTIVE) {
                // perspective
                set('near_origin', worldTransform.getTranslation());
                set('near_x', Vec3.ZERO);
                set('near_y', Vec3.ZERO);
            } else {
                // orthographic
                set('near_origin', points[3]);
                set('near_x', va.sub2(points[0], points[3]));
                set('near_y', va.sub2(points[2], points[3]));
            }

            // far
            set('far_origin', points[7]);
            set('far_x', va.sub2(points[4], points[7]));
            set('far_y', va.sub2(points[6], points[7]));
        };

        // temp control of camera start
        const url = new URL(location.href);
        const focal = url.searchParams.get('focal');
        if (focal) {
            const parts = focal.toString().split(',');
            if (parts.length === 3) {
                this.setFocalPoint(new Vec3(parseFloat(parts[0]), parseFloat(parts[1]), parseFloat(parts[2])), 0);
            }
        }
        const angles = url.searchParams.get('angles');
        if (angles) {
            const parts = angles.toString().split(',');
            if (parts.length === 2) {
                this.setAzimElev(parseFloat(parts[0]), parseFloat(parts[1]), 0);
            }
        }
        const distance = url.searchParams.get('distance');
        if (distance) {
            this.setDistance(parseFloat(distance), 0);
        }
    }

    remove() {
        const { scene } = this;

        this.controller.destroy();
        this.controller = null;

        // cleanup render passes
        this.clearPass?.destroy();
        this.stochasticClearPass?.destroy();
        this.warpClearPass?.destroy();
        this.mainPass?.destroy();
        this.splatPass?.destroy();
        this.stochasticSplatPass?.destroy();
        this.warpSplatPass?.destroy();
        (this.stochasticResolvePass?.renderable as ShaderQuad)?.destroy();
        this.stochasticResolvePass?.destroy();
        this.stochasticTarget?.colorBuffer.destroy();
        this.stochasticTarget?.destroy();
        this.warpTarget?.depthBuffer.destroy();
        this.warpTarget?.destroy();
        this.gizmoPass?.destroy();
        this.depthReducePass?.destroy();
        this.finalPass?.destroy();
        this.camera.framePasses = null;

        scene.cameraRoot.removeChild(this.mainCamera);

        this.picker.destroy();
        this.picker = null;

        scene.events.off('scene.boundChanged', this.onBoundChanged, this);
    }

    // handle the scene's bound changing. the camera must be configured to render
    // the entire extents as well as possible.
    // also update the existing camera distance to maintain the current view
    onBoundChanged(bound: BoundingBox) {
        const prevDistance = this.distanceTween.value.distance * this.sceneRadius;
        this.sceneRadius = Math.max(1e-03, bound.halfExtents.length());
        this.setDistance(prevDistance / this.sceneRadius, 0);
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.worldTransform.data);
        serializer.pack(
            this.fov,
            this.tonemapping,
            this.targetSize.width,
            this.targetSize.height
        );
    }

    // handle the viewer canvas resizing
    rebuildRenderTargets() {
        const { width, height } = this.targetSize;
        const { mainTarget, scene } = this;

        // early out if size is unchanged
        if (mainTarget && mainTarget.width === width && mainTarget.height === height) {
            return;
        }

        if (!mainTarget) {
            // first time - construct render targets
            const { graphicsDevice } = scene;

            const createTexture = (name: string, width: number, height: number, format: number) => {
                return new Texture(graphicsDevice, {
                    name,
                    width,
                    height,
                    format,
                    mipmaps: false,
                    minFilter: FILTER_NEAREST,
                    magFilter: FILTER_NEAREST,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE
                });
            };

            const colorBuffer = createTexture('cameraColor', width, height, PIXELFORMAT_RGBA16F);
            const workBuffer = createTexture('workColor', width, height, PIXELFORMAT_RGBA8);
            const depthBuffer = createTexture('cameraDepth', width, height, PIXELFORMAT_DEPTH);

            // create main render target
            this.mainTarget = new RenderTarget({
                colorBuffer,
                depthBuffer,
                autoResolve: false
            });

            // create MRT render target for splat pass
            this.splatTarget = new RenderTarget({
                colorBuffers: [
                    colorBuffer,        // RT0: main color (shared)
                    workBuffer          // RT1: overlay output (shared with workTarget)
                ],
                depthBuffer,
                autoResolve: false
            });

            // Stochastic samples need no blend accumulation. Both projections
            // share one RGBA8 colour buffer, resolved over the world pass.
            const stochasticColor = createTexture('stochasticSplats', width, height, PIXELFORMAT_RGBA8);
            stochasticColor.minFilter = FILTER_LINEAR;
            stochasticColor.magFilter = FILTER_LINEAR;
            this.stochasticTarget = new RenderTarget({
                colorBuffers: [stochasticColor, workBuffer],
                depthBuffer,
                autoResolve: false
            });

            this.colorTarget = new RenderTarget({
                colorBuffer,
                depth: false,
                autoResolve: false
            });

            // create work buffer (used for picking, overlay output, and other operations)
            this.workTarget = new RenderTarget({
                colorBuffer: workBuffer,
                depth: false,
                autoResolve: false
            });

            // set picker render targets
            this.picker.setRenderTargets(this.colorTarget, this.workTarget);

            // clear all targets
            this.clearPass.init(this.splatTarget);
            this.clearPass.setClearColor(new Color(0, 0, 0, 0));
            this.clearPass.setClearDepth(1);
            this.clearPass.setClearStencil(0);

            // configure main pass - world layer with clears
            this.mainPass.init(this.mainTarget);
            this.mainPass.addLayer(this.camera, scene.worldLayer, false, false);
            this.mainPass.addLayer(this.camera, scene.worldLayer, true, false);

            // configure splat pass - MRT target, no clears
            this.splatPass.init(this.splatTarget);
            this.splatPass.addLayer(this.camera, scene.splatLayer, false, false);
            this.splatPass.addLayer(this.camera, scene.splatLayer, true, false);

            this.stochasticSplatPass.init(this.stochasticTarget);
            this.stochasticSplatPass.addLayer(this.camera, scene.splatLayer, false, false);
            this.stochasticSplatPass.addLayer(this.camera, scene.splatLayer, true, false);
            this.stochasticClearPass.init(this.stochasticTarget);
            this.stochasticClearPass.setClearColor(new Color(0, 0, 0, 0));
            this.stochasticResolvePass.init(this.colorTarget);

            // configure gizmo pass. the centers and gizmo layers each clear depth
            // before their opaque step, after the depth-independent tool overlay,
            // so centers depth-test against each other alone and the gizmos then
            // start from a clean buffer again
            this.gizmoPass.init(this.mainTarget);
            this.gizmoPass.addLayer(this.camera, scene.overlayLayer, false, false);
            this.gizmoPass.addLayer(this.camera, scene.overlayLayer, true, false);
            this.gizmoPass.addLayer(this.camera, scene.centersLayer, false, true);
            this.gizmoPass.addLayer(this.camera, scene.centersLayer, true, false);
            this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, false, true);
            this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, true, false);

            this.finalPass.init(null);

            // assign render passes to camera
            this.camera.framePasses = [this.clearPass, this.stochasticClearPass, this.mainPass, this.splatPass,
                this.stochasticSplatPass, this.warpClearPass, this.warpSplatPass, this.depthReducePass, this.stochasticResolvePass,
                this.gizmoPass, this.finalPass];
        } else {
            // resize existing render targets
            const { splatTarget, colorTarget, workTarget } = this;

            mainTarget.resize(width, height);
            workTarget.resize(width, height);
            colorTarget.resize(width, height);
            splatTarget.resize(width, height);
            this.stochasticTarget.resize(width, height);
            this.warpTarget?.resize(width, height);
        }

        this.camera.horizontalFov = width > height;
        this.camera.aspectRatio = width / height;
        scene.events.fire('camera.resize', { width, height });
    }

    onUpdate(deltaTime: number) {
        // controller update
        this.controller.update(deltaTime);

        // update underlying values
        this.focalPointTween.update(deltaTime);
        this.azimElevTween.update(deltaTime);
        this.distanceTween.update(deltaTime);

        const azimElev = this.azimElevTween.value;
        const distance = this.distanceTween.value;

        Camera.calcForwardVec(forwardVec, azimElev.azim, azimElev.elev);

        if (this.lookCameraPos) {
            cameraPosition.copy(this.lookCameraPos);
            if (this.azimElevTween.timer >= this.azimElevTween.transitionTime) {
                this.lookCameraPos = null;
            }
        } else {
            cameraPosition.copy(forwardVec);
            cameraPosition.mulScalar(distance.distance * this.sceneRadius / this.fovFactor);
            cameraPosition.add(this.focalPointTween.value);
        }

        if (this.poseOverride) {
            // cameraRoot has identity transform, so local space is world space
            const { position, rotation, fov, near, far } = this.poseOverride;
            this.mainCamera.setLocalPosition(position);
            this.mainCamera.setLocalRotation(rotation);
            this.camera.fov = fov;
            this.near = near;
            this.far = far;
        } else {
            this.mainCamera.setLocalPosition(cameraPosition);
            this.mainCamera.setLocalEulerAngles(azimElev.elev, azimElev.azim, 0);

            this.fitClippingPlanes(this.mainCamera.getLocalPosition(), this.mainCamera.forward);

            this.displayTransform.copy(this.mainCamera.getWorldTransform());
        }

        const { camera } = this.mainCamera;
        const { targetSize } = this;

        // update ortho height
        camera.orthoHeight = this.distanceTween.value.distance * this.sceneRadius / this.fovFactor * (this.fov / 90) * (camera.horizontalFov ? targetSize.height / targetSize.width : 1);
        camera.camera._updateViewProjMat();
    }

    fitClippingPlanes(cameraPosition: Vec3, forwardVec: Vec3) {
        const bound = this.scene.bound;
        const boundRadius = bound.halfExtents.length();

        vec.sub2(bound.center, cameraPosition);
        const dist = vec.dot(forwardVec);

        if (this.ortho) {
            // orthographic has no perspective divide, so the near plane can sit
            // behind the camera. Span the whole scene bound (near goes negative
            // when the camera is inside it) so scene content is never clipped in
            // front of or behind the camera.
            const radius = Math.max(boundRadius, 1e-2);
            this.far = dist + radius;
            this.near = dist - radius;
        } else {
            const far = Math.max(dist + boundRadius, 1e-2);
            const near = Math.max(dist - boundRadius, far / (1024 * 16));

            this.far = far;
            this.near = Math.min(1.0, near);
        }
    }

    onPreRender() {
        this.rebuildRenderTargets();
        // Allocate the experimental warp's private depth only when first used.
        if (this.scene.warpedRender && !this.warpTarget) {
            const { scene } = this;
            const { width, height } = this.targetSize;
            this.warpTarget = new RenderTarget({
                colorBuffers: [this.stochasticTarget.colorBuffer, this.workTarget.colorBuffer],
                depthBuffer: new Texture(scene.graphicsDevice, {
                    name: 'warpedDepth',
                    width,
                    height,
                    format: PIXELFORMAT_DEPTH,
                    mipmaps: false,
                    minFilter: FILTER_NEAREST,
                    magFilter: FILTER_NEAREST,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE
                }),
                autoResolve: false
            });
            this.warpClearPass.init(this.warpTarget);
            this.warpClearPass.setClearDepth(1);
            this.warpSplatPass.init(this.warpTarget);
            this.warpSplatPass.addLayer(this.camera, scene.splatLayer, false, false);
            this.warpSplatPass.addLayer(this.camera, scene.splatLayer, true, false);
        }
        this.splatPass.enabled = !this.scene.movingRender;
        this.stochasticClearPass.enabled = this.scene.movingRender;
        this.stochasticSplatPass.enabled = this.scene.movingRender && !this.scene.warpedRender;
        this.warpClearPass.enabled = this.scene.warpedRender;
        this.warpSplatPass.enabled = this.scene.warpedRender;
        this.stochasticResolvePass.enabled = this.scene.movingRender;
        this.updateCameraUniforms();
    }

    onPostRender() {

    }

    focus(options?: { focalPoint: Vec3, radius: number, speed: number }) {
        const getSplatFocalPoint = () => {
            for (const element of this.scene.elements) {
                if (element.type === ElementType.splat) {
                    const focalPoint = (element as Splat).focalPoint?.();
                    if (focalPoint) {
                        return focalPoint;
                    }
                }
            }
        };

        const focalPoint = options ? options.focalPoint : (getSplatFocalPoint() ?? this.scene.bound.center);
        const focalRadius = options ? options.radius : this.scene.bound.halfExtents.length();

        const fdist = focalRadius / this.sceneRadius;

        this.setDistance(isFinite(fdist) ? fdist : 1, options?.speed ?? 0);
        this.setFocalPoint(focalPoint, options?.speed ?? 0);
    }

    get fovFactor() {
        // use the larger axis fov (which is always this.fov) so camera distance
        // stays constant regardless of viewport aspect ratio.
        return Math.sin(this.fov * math.DEG_TO_RAD * 0.5);
    }

    // world size of one screen pixel at the given view depth (ortho is
    // depth-independent)
    worldSizePerPixel(depth: number) {
        const pixelScale = (2 / this.camera.projectionMatrix.data[5]) / Math.max(1, this.scene.canvas.clientHeight);
        return this.ortho ? pixelScale : pixelScale * depth;
    }

    captureFrame(): CameraFrame {
        const { camera, mainCamera, scene } = this;
        const { width, height } = this.targetSize;
        // the camera can have moved since the last rendered frame (held or
        // skipped frames still run onUpdate) and the component only dirties
        // its cached view matrix in this prerender hook, so refresh it or the
        // captured view can disagree with the pose
        camera.onAppPrerender();
        return {
            position: mainCamera.getPosition().clone(),
            rotation: mainCamera.getRotation().clone(),
            fov: camera.fov,
            ortho: this.ortho,
            orthoHeight: camera.orthoHeight,
            near: camera.nearClip,
            far: camera.farClip,
            horizontalFov: camera.horizontalFov,
            aspectRatio: camera.aspectRatio,
            projection: camera.projectionMatrix.clone(),
            view: camera.viewMatrix.clone(),
            viewport: { width: scene.canvas.clientWidth, height: scene.canvas.clientHeight },
            target: { width, height },
            pixelScale: this.worldSizePerPixel(1)
        };
    }

    // run fn with the engine camera set to the frame and restore it before
    // returning. The pick passes (the projector's sorted re-projection and
    // RenderPassPicker) read the engine camera directly and can't take
    // matrices, so this is how a captured frame reaches them. Synchronous
    // only: the live camera is never left swapped across an await.
    withFrame(frame: CameraFrame, fn: () => void) {
        const { camera, mainCamera } = this;
        const livePosition = mainCamera.getPosition().clone();
        const liveRotation = mainCamera.getRotation().clone();
        const live = {
            fov: camera.fov,
            projection: camera.projection,
            orthoHeight: camera.orthoHeight,
            nearClip: camera.nearClip,
            farClip: camera.farClip,
            horizontalFov: camera.horizontalFov,
            aspectRatio: camera.aspectRatio
        };
        // the component's fields directly: the ortho setter fires an event
        mainCamera.setPosition(frame.position);
        mainCamera.setRotation(frame.rotation);
        camera.fov = frame.fov;
        camera.projection = frame.ortho ? PROJECTION_ORTHOGRAPHIC : PROJECTION_PERSPECTIVE;
        camera.orthoHeight = frame.orthoHeight;
        camera.nearClip = frame.near;
        camera.farClip = frame.far;
        camera.horizontalFov = frame.horizontalFov;
        camera.aspectRatio = frame.aspectRatio;
        // the component caches its view matrix and only dirties it in this
        // prerender hook, once per rendered frame, so a pose set here would
        // otherwise read as the previous frame's view
        camera.onAppPrerender();
        try {
            fn();
        } finally {
            mainCamera.setPosition(livePosition);
            mainCamera.setRotation(liveRotation);
            camera.fov = live.fov;
            camera.projection = live.projection;
            camera.orthoHeight = live.orthoHeight;
            camera.nearClip = live.nearClip;
            camera.farClip = live.farClip;
            camera.horizontalFov = live.horizontalFov;
            camera.aspectRatio = live.aspectRatio;
            camera.onAppPrerender();
        }
    }

    // the picker renders into this camera's colour and work buffers, so no
    // frame may render between a pick pass and its readbacks (see
    // Scene.renderHold). Frames dropped during the hold are owed one.
    private async holdFrames<T>(fn: () => Promise<T>): Promise<T> {
        const { scene } = this;
        scene.renderHold++;
        try {
            return await fn();
        } finally {
            if (--scene.renderHold === 0) {
                scene.forceRender = true;
            }
        }
    }

    // pick ray at normalized screen coordinates (0-1) under the frame. The
    // origin is the camera in perspective and a point on the near plane in
    // ortho, so callers measure each origin's own view depth rather than
    // assuming near.
    private frameRay(frame: CameraFrame, x: number, y: number, ray: Ray) {
        invViewProj.mul2(frame.projection, frame.view).invert();
        const unproject = (z: number, out: Vec3) => {
            vec4.set(x * 2 - 1, 1 - y * 2, z, 1);
            invViewProj.transformVec4(vec4, vec4);
            return out.set(vec4.x / vec4.w, vec4.y / vec4.w, vec4.z / vec4.w);
        };
        unproject(1, vec);
        if (frame.ortho) {
            unproject(-1, vecb);
            ray.set(vecb, vec.sub(vecb).normalize());
        } else {
            ray.set(frame.position, vec.sub(frame.position).normalize());
        }
    }

    // depth-pick normalized screen points against the splats under the frame:
    // the closest surface at each point as world position, along-view distance
    // and linear depth, or null where nothing is hit. The depth pass is
    // rendered once per splat for the whole batch, which keeps sampled brush
    // strokes practical. Runs in the caller's command slot; intersectMany is
    // the queue entry for callers that don't own one.
    async pickDepths(
        ctx: CommandContext,
        frame: CameraFrame,
        points: { x: number, y: number }[],
        splats = this.scene.getElementsByType(ElementType.splat) as Splat[]
    ) {
        const { scene } = this;
        const forward = frame.rotation.transformVector(Vec3.FORWARD, new Vec3());

        const rays = points.map(({ x, y }) => {
            this.frameRay(frame, x, y, ray);
            return {
                origin: ray.origin.clone(),
                direction: ray.direction.clone(),
                cosAngle: ray.direction.dot(forward),
                originDepth: vecb.sub2(ray.origin, frame.position).dot(forward)
            };
        });

        const closestDepths = points.map(() => Infinity);
        const closestSplats: (Splat | null)[] = new Array(points.length).fill(null);

        await this.holdFrames(() => this.picker.withTargetSize('depth', frame.target, async () => {
            // Find the splat with the smallest depth at each screen position.
            // Each depth pass composites through the projected cache front to
            // back, so it needs a sorted order under it, projected under the
            // same frame
            for (const splat of splats) {
                // removed while the batch was queued
                if (!splat.scene) continue;

                this.withFrame(frame, () => {
                    scene.projectedSplatRenderer.renderSortedForPick(frame.target);
                    this.picker.prepareDepth(splat);
                });
                const depths = await this.picker.readDepths(points);
                for (let j = 0; j < depths.length; ++j) {
                    const depth = depths[j];
                    if (depth !== null && depth < closestDepths[j]) {
                        closestDepths[j] = depth;
                        closestSplats[j] = splat;
                    }
                }
            }
        }));

        const { near, far } = frame;
        return points.map((point, index) => {
            const splat = closestSplats[index];
            if (!splat) {
                return null;
            }

            // Convert normalized depth to linear depth
            const linearDepth = closestDepths[index] * (far - near) + near;

            // Calculate world position from the snapshotted ray and view depth
            const { origin, direction, cosAngle, originDepth } = rays[index];
            const t = (linearDepth - originDepth) / cosAngle;
            const position = new Vec3();
            position.copy(origin).add(vec.copy(direction).mulScalar(t));

            // dolly distance for the caller: the along-view distance to the surface,
            // |linearDepth| / cosAngle. abs keeps behind-camera ortho depths positive
            // (a negative distance would clamp to minZoom and collapse the view), and
            // dividing by cosAngle reproduces perspective's ray distance unchanged.
            // Deliberately the along-view distance, not position.distance(cameraPos):
            // the latter includes the lateral offset for an off-axis ortho pick, which
            // would couple orthoHeight to where in the viewport the click landed.
            const distance = Math.abs(linearDepth) / cosAngle;

            return { splat, position, distance, depth: linearDepth };
        });
    }

    // id-pick a normalized screen rectangle against one splat under the frame:
    // the frontmost instance index per render-target pixel (0xffffffff where
    // none), filtered by the selection op as the pick shader does. Runs in the
    // caller's command slot.
    pickIds(
        ctx: CommandContext,
        frame: CameraFrame,
        splat: Splat,
        mode: 'add' | 'remove' | 'set' | 'intersect',
        footprint: number,
        x: number,
        y: number,
        width: number,
        height: number
    ) {
        return this.holdFrames(() => this.picker.withTargetSize('id', frame.target, () => {
            this.withFrame(frame, () => this.picker.prepareId(splat, mode, footprint));
            return this.picker.readIds(x, y, width, height);
        }));
    }

    // depth-pick from outside the command queue: captures the live frame now
    // and takes a slot, so the pick is ordered with every other command and
    // runs under the camera the caller saw, however long the queue delays it
    intersectMany(points: { x: number, y: number }[], splats?: Splat[]) {
        const frame = this.captureFrame();
        const pickPoints = points.map(({ x, y }) => ({ x, y }));
        const pickSplats = splats?.slice();
        return this.scene.commandQueue.enqueue(ctx => this.pickDepths(ctx, frame, pickPoints, pickSplats));
    }

    // intersect the scene at the given normalized screen coordinate (0-1 range) using depth picking
    async intersect(x: number, y: number) {
        return (await this.intersectMany([{ x, y }]))[0];
    }

    // intersect the scene at the normalized screen location (0-1 range) and focus the camera on this location
    async pickFocalPoint(x: number, y: number) {
        const result = await this.intersect(x, y);
        if (result) {
            const { scene } = this;

            this.setFocalPoint(result.position);
            this.setDistance(result.distance / this.sceneRadius * this.fovFactor);
            scene.events.fire('camera.focalPointPicked', {
                camera: this,
                splat: result.splat,
                position: result.position
            });
        }
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];

        return {
            focalPoint: pack3(this.focalPointTween.target),
            azim: this.azim,
            elev: this.elevation,
            distance: this.distance,
            fov: this.fov,
            tonemapping: this.tonemapping
        };
    }

    docDeserialize(settings: any) {
        this.setFocalPoint(new Vec3(settings.focalPoint), 0);
        this.setAzimElev(settings.azim, settings.elev, 0);
        this.setDistance(settings.distance, 0);
        this.fov = settings.fov;
        this.tonemapping = settings.tonemapping;
    }

    // offscreen render mode

    startOffscreenMode(width: number, height: number) {
        this.targetSizeOverride = { width, height };
        this.finalPass.enabled = false;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    endOffscreenMode() {
        this.targetSizeOverride = null;
        this.finalPass.enabled = true;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    get targetSize() {
        return this.targetSizeOverride ?? this.scene.targetSize;
    }

    get camera() {
        return this.mainCamera.camera;
    }

    get worldTransform() {
        return this.mainCamera.getWorldTransform();
    }

    get position() {
        return this.mainCamera.getPosition();
    }

    get forward() {
        return this.mainCamera.forward;
    }
}

export { Camera, CameraFrame };
